import React, { useCallback, useState } from 'react';
import { Modal, Button, Alert, Row, Segmented } from 'antd';
import PropTypes from 'prop-types';
import Input from '../elements/input/Input';
import { FloatingLabelSearchSelect } from '../elements/floatingLabelInput/FloatingLabelInput';
import { toast } from 'react-toastify';
import agentService from '../../api/agent-api';
import { useInfiniteOptions, withSelectedOption } from '../../hooks/useInfiniteOptions';

const CreateAgentModal = ({ isOpen, onClose, onCreate }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // 'new' creates a brand-new account; 'existing' promotes a plain user who already
    // has one. Promotion keeps their password, so no password is asked for and the
    // other account fields are shown read-only. The referral code is prefilled from
    // the account but stays editable.
    const [mode, setMode] = useState('new');
    const isExistingMode = mode === 'existing';

    const [selectedUser, setSelectedUser] = useState(null);

    const fetchUserPage = useCallback(async (page, limit, search) => {
        const { data, success } = await agentService.listPromotableUsers({ page, limit, search: search || null });
        if (!success) return null;
        return { items: data?.users || [], totalPages: data?.pagination?.totalPages };
    }, []);
    const {
        items: loadedUsers,
        loading: usersLoading,
        onSearch: setUserSearch,
        onPopupScroll: onUsersScroll,
    } = useInfiniteOptions(fetchUserPage, {
        enabled: isOpen && isExistingMode,
        reloadKey: isOpen,
    });
    const userOptions = withSelectedOption(loadedUsers, selectedUser, '_id');

    const [modalForm, setModalForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        referralCode: '',
    });

    const resetForm = () => {
        setModalForm({ firstName: '', lastName: '', email: '', password: '', referralCode: '' });
        setError('');
        setSelectedUser(null);
        setUserSearch('');
    };


    const handleModeChange = (value) => {
        setMode(value);
        setError('');
        resetForm();
    };

    const handleSelectUser = (userId) => {
        const user = userOptions.find((u) => u._id === userId) || null;
        setSelectedUser(user);
        // Prefilled with the code the account already has — stays editable, so an
        // admin can swap it for a nicer one while promoting.
        setModalForm((prev) => ({ ...prev, referralCode: user?.referralCode || '' }));
        setError('');
    };

    const handleOnChange = (e) => {
        const { name, value } = e.target;
        setModalForm((prev) => ({ ...prev, [name]: value }));
    };

    const validateInputs = () => {
        const { firstName, lastName, email, password } = modalForm;

        if (!firstName.trim()) {
            setError('Unesite ime agenta.');
            return false;
        }
        if (!lastName.trim()) {
            setError('Unesite prezime agenta.');
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Unesite validnu adresu e-pošte.');
            return false;
        }
        if (!password || password.length < 8) {
            setError('Lozinka mora imati najmanje 8 karaktera.');
            return false;
        }

        setError('');
        return true;
    };

    const handleOk = async () => {
        setLoading(true);
        setError('');

        try {
            if (isExistingMode) {
                if (!selectedUser) {
                    setError('Izaberite korisnika koji postaje agent.');
                    setLoading(false);
                    return;
                }

                toast.loading('Postavljanje agenta...');
                const payload = { userId: selectedUser._id };
                if (modalForm.referralCode.trim()) payload.referralCode = modalForm.referralCode.trim();
                const response = await agentService.promote(payload);

                if (!response.success && response.success !== undefined) {
                    toast.dismiss();
                    toast.error(response.message || 'Došlo je do greške pri postavljanju agenta.');
                    setError(response.message || 'Došlo je do greške pri postavljanju agenta.');
                    return;
                }
            } else {
                if (!validateInputs()) {
                    setLoading(false);
                    return;
                }

                toast.loading('Kreiranje agenta...');
                const payload = { ...modalForm };
                if (!payload.referralCode.trim()) delete payload.referralCode;
                const response = await agentService.create(payload);

                if (!response.success && response.success !== undefined) {
                    toast.dismiss();
                    toast.error(response.message || 'Došlo je do greške pri kreiranju agenta.');
                    setError(response.message || 'Došlo je do greške pri kreiranju agenta.');
                    return;
                }
            }

            onCreate();
            onClose();

            setTimeout(() => {
                toast.dismiss();
                toast.success('Agent uspešno kreiran.');
            }, 500);

            resetForm();
        } catch (err) {
            console.error('Error creating agent:', err);
            toast.dismiss();
            toast.error('Došlo je do greške pri kreiranju agenta.');
            setError(err.message || 'Došlo je do greške pri kreiranju agenta.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        resetForm();
        setMode('new');
        onClose();
    };

    return (
        <Modal
            className="create_user_modal"
            title="Dodaj agenta"
            open={isOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={[
                <Button
                    key="create"
                    type="primary"
                    onClick={handleOk}
                    className="btn-primary"
                    disabled={loading}
                    style={{ width: '150px' }}
                >
                    {loading ? 'Kreiranje...' : 'Dodaj agenta'}
                </Button>,
            ]}
        >
            <form
                className="create_user_modal_form"
                layout="vertical"
                autoComplete="off"
            >
                <Segmented
                    block
                    value={mode}
                    onChange={handleModeChange}
                    options={[
                        { label: 'Novi agent', value: 'new' },
                        { label: 'Postojeći korisnik', value: 'existing' },
                    ]}
                    style={{ marginBottom: 16 }}
                />

                {isExistingMode ? (
                    <Row className="create_user_modal_row">
                        <div>
                            <FloatingLabelSearchSelect
                                label="Korisnik"
                                placeholder="Pretraži po imenu ili e-pošti"
                                value={selectedUser?._id || null}
                                onChange={handleSelectUser}
                                onSearch={setUserSearch}
                                loading={usersLoading}
                                onPopupScroll={onUsersScroll}
                                options={userOptions.map((user) => ({
                                    value: user._id,
                                    label: `${user.firstName} ${user.lastName} (${user.email})`,
                                }))}
                            />
                        </div>

                        {selectedUser && (
                            <>
                                <div>
                                    <Input label="Ime" id="existingFirstName" value={selectedUser.firstName || ''} readOnly disabled />
                                </div>
                                <div>
                                    <Input label="Prezime" id="existingLastName" value={selectedUser.lastName || ''} readOnly disabled />
                                </div>
                                <div>
                                    <Input label="Adresa e-pošte" id="existingEmail" value={selectedUser.email || ''} readOnly disabled />
                                </div>
                                <div>
                                    <Input label="Broj telefona" id="existingPhone" value={selectedUser.phone || ''} readOnly disabled />
                                </div>
                                {/* Prefilled with the code the account already has. Changing it is
                                    allowed, but note referrals already made under the old code point
                                    at this user by id, not by code, so they are unaffected. */}
                                <div>
                                    <Input
                                        label="Referral kod"
                                        id="referralCode"
                                        name="referralCode"
                                        placeholder="Referral kod"
                                        value={modalForm.referralCode}
                                        onChange={handleOnChange}
                                    />
                                </div>
                            </>
                        )}
                    </Row>
                ) : (
                    <Row className="create_user_modal_row">
                        <div>
                            <Input
                                label="Ime"
                                id="firstName"
                                name="firstName"
                                placeholder="Ime"
                                value={modalForm.firstName}
                                onChange={handleOnChange}
                            />
                        </div>
                        <div>
                            <Input
                                label="Prezime"
                                id="lastName"
                                name="lastName"
                                placeholder="Prezime"
                                value={modalForm.lastName}
                                onChange={handleOnChange}
                            />
                        </div>
                        <div>
                            <Input
                                type="email"
                                required
                                autoComplete="new-email"
                                label="Adresa e-pošte"
                                id="email"
                                name="email"
                                placeholder="Adresa e-pošte"
                                value={modalForm.email}
                                onChange={handleOnChange}
                            />
                        </div>
                        <div>
                            <Input
                                type="password"
                                autoComplete="new-password"
                                label="Lozinka"
                                id="password"
                                name="password"
                                placeholder="Lozinka"
                                value={modalForm.password}
                                onChange={handleOnChange}
                            />
                        </div>
                        <div>
                            <Input
                                label="Referral kod (opciono)"
                                id="referralCode"
                                name="referralCode"
                                placeholder="Automatski generisan ako se ostavi prazno"
                                value={modalForm.referralCode}
                                onChange={handleOnChange}
                            />
                        </div>
                    </Row>
                )}
                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
            </form>
        </Modal>
    );
};

CreateAgentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreate: PropTypes.func.isRequired,
};

export default CreateAgentModal;
